import { useState, useRef, useCallback } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChevronRight,
  Upload,
  X,
  Loader2,
  Settings,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import {
  cpCreateLineOARichMenu,
  cpUploadLineOARichMenuImage,
  cpDeleteLineOARichMenu,
} from '@/lib/api/controlPlaneClient'
import { useQueryClient } from '@tanstack/react-query'
import { LINE_OA_RICH_MENUS_KEY } from './useLineOARichMenus'

// --- Constants ---

type MenuSize = 'full' | 'half'

const FULL_SIZES: [number, number][] = [
  [2500, 1686], [2500, 843], [1200, 810],
  [1200, 405], [800, 540], [800, 270],
]

const HALF_SIZES: [number, number][] = [
  [2500, 843], [1200, 405], [800, 270],
]

const GRID_TEMPLATES: Record<string, { label: string; cols: number; rows: number }> = {
  '1x1': { label: '1×1 (full image)', cols: 1, rows: 1 },
  '1x2': { label: '1×2 (2 columns)', cols: 2, rows: 1 },
  '2x1': { label: '2×1 (2 rows)', cols: 1, rows: 2 },
  '1x3': { label: '1×3 (3 columns)', cols: 3, rows: 1 },
  '3x1': { label: '3×1 (3 rows)', cols: 1, rows: 3 },
  '2x3': { label: '2×3 (6 areas)', cols: 3, rows: 2 },
}

// --- Helpers ---

function getValidSizes(menuSize: MenuSize) {
  return menuSize === 'full' ? FULL_SIZES : HALF_SIZES
}

function computeAreaBounds(
  template: { cols: number; rows: number },
  imageWidth: number,
  imageHeight: number,
) {
  const { cols, rows } = template
  const areas: { x: number; y: number; width: number; height: number }[] = []
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = Math.round((col / cols) * imageWidth)
      const y = Math.round((row / rows) * imageHeight)
      const nextX = col === cols - 1 ? imageWidth : Math.round(((col + 1) / cols) * imageWidth)
      const nextY = row === rows - 1 ? imageHeight : Math.round(((row + 1) / rows) * imageHeight)
      areas.push({ x, y, width: nextX - x, height: nextY - y })
    }
  }
  return areas
}

function validateImageFile(
  file: File,
  menuSize: MenuSize,
): Promise<{ valid: boolean; error?: string; width?: number; height?: number }> {
  return new Promise((resolve) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      resolve({ valid: false, error: 'Image must be JPEG or PNG format.' })
      return
    }
    if (file.size > 1_048_576) {
      resolve({ valid: false, error: 'Image must be 1MB or less.' })
      return
    }
    const img = new Image()
    img.onload = () => {
      const validSizes = getValidSizes(menuSize)
      const match = validSizes.some(([w, h]) => img.naturalWidth === w && img.naturalHeight === h)
      if (!match) {
        const sizeList = validSizes.map(([w, h]) => `${w}×${h}`).join(', ')
        resolve({
          valid: false,
          error: `Image dimensions must be one of: ${sizeList}. Got ${img.naturalWidth}×${img.naturalHeight}.`,
        })
      } else {
        resolve({ valid: true, width: img.naturalWidth, height: img.naturalHeight })
      }
    }
    img.onerror = () => resolve({ valid: false, error: 'Could not read image file.' })
    img.src = URL.createObjectURL(file)
  })
}

// --- Area action types ---

type AreaActionType = 'uri' | 'message'

interface AreaConfig {
  actionType: AreaActionType
  value: string
}

// --- Editor Component ---

export function LineOARichMenuEditor() {
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Settings state
  const [menuName, setMenuName] = useState('')
  const [chatBarText, setChatBarText] = useState('Tap to open')
  const [menuSize, setMenuSize] = useState<MenuSize>('full')
  const [selected, setSelected] = useState(true) // default state: expanded

  // Image state
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  // Tap areas state
  const [gridTemplate, setGridTemplate] = useState('2x3')
  const [hoveredArea, setHoveredArea] = useState<number | null>(null)
  const [areaConfigs, setAreaConfigs] = useState<AreaConfig[]>(
    Array.from({ length: 6 }, () => ({ actionType: 'uri' as AreaActionType, value: '' })),
  )

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Computed
  const template = GRID_TEMPLATES[gridTemplate]
  const areaCount = template.cols * template.rows

  // --- Image handling ---

  const handleImageFile = useCallback(async (file: File) => {
    setImageError(null)
    const result = await validateImageFile(file, menuSize)
    if (!result.valid) {
      setImageError(result.error || 'Invalid image.')
      return
    }
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setImageDimensions({ width: result.width!, height: result.height! })
  }, [menuSize])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleImageFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleImageFile(file)
  }

  const clearImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setImageDimensions(null)
    setImageError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // When menu size changes, clear image (dimensions may no longer be valid)
  const handleMenuSizeChange = (newSize: MenuSize) => {
    setMenuSize(newSize)
    if (imageFile) {
      clearImage()
    }
  }

  // When grid template changes, update area configs
  const handleGridTemplateChange = (newTemplate: string) => {
    setGridTemplate(newTemplate)
    const newCount = GRID_TEMPLATES[newTemplate].cols * GRID_TEMPLATES[newTemplate].rows
    setAreaConfigs((prev) => {
      const next = Array.from({ length: newCount }, (_, i) =>
        prev[i] || { actionType: 'uri' as AreaActionType, value: '' },
      )
      return next
    })
  }

  const updateAreaConfig = (index: number, field: keyof AreaConfig, value: string) => {
    setAreaConfigs((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  // --- Form validation ---

  const getValidationErrors = (): string[] => {
    const errors: string[] = []
    if (!menuName.trim()) errors.push('Menu name is required.')
    if (!chatBarText.trim()) errors.push('Chat bar text is required.')
    if (chatBarText.length > 14) errors.push('Chat bar text must be 14 characters or fewer.')
    if (!imageFile || !imageDimensions) errors.push('Menu image is required.')
    for (let i = 0; i < areaCount; i++) {
      const cfg = areaConfigs[i]
      if (!cfg?.value.trim()) {
        errors.push(`Area ${i + 1} action value is required.`)
      }
    }
    return errors
  }

  // --- Submit ---

  const handleSubmit = async () => {
    const errors = getValidationErrors()
    if (errors.length > 0) {
      toast.error(errors[0])
      return
    }

    if (!imageFile || !imageDimensions) return

    setIsSubmitting(true)

    try {
      // Build LINE rich menu definition
      const bounds = computeAreaBounds(template, imageDimensions.width, imageDimensions.height)
      const areas = bounds.map((b, i) => {
        const cfg = areaConfigs[i]
        const action: Record<string, string> =
          cfg.actionType === 'uri'
            ? { type: 'uri', uri: cfg.value }
            : { type: 'message', text: cfg.value }
        return { bounds: b, action }
      })

      const menuDefinition = {
        size: { width: imageDimensions.width, height: imageDimensions.height },
        selected,
        name: menuName.trim(),
        chatBarText: chatBarText.trim(),
        areas,
      }

      // Step 1: Create menu definition
      const { rich_menu_id } = await cpCreateLineOARichMenu(menuDefinition)

      // Step 2: Upload image
      try {
        await cpUploadLineOARichMenuImage(rich_menu_id, imageFile)
      } catch (uploadErr) {
        // Clean up orphan menu if image upload fails
        try {
          await cpDeleteLineOARichMenu(rich_menu_id)
        } catch {
          // Best effort cleanup
        }
        throw uploadErr
      }

      // Success
      queryClient.invalidateQueries({ queryKey: LINE_OA_RICH_MENUS_KEY })
      toast.success('Rich menu created.')
      navigate('/line-oa/rich-menus')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create rich menu.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Connection guard ---

  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  if (!status?.connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">New Rich Menu</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <Settings className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">Connect LINE Messaging API</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect your LINE Messaging API token in Storefront Settings first.
                </p>
              </div>
              <Button asChild>
                <Link to="/storefront/settings">Go to Settings</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  // --- Render ---

  const validSizes = getValidSizes(menuSize)

  return (
    <PageContainer>
      <div className="p-6 space-y-6 max-w-3xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/line-oa" className="hover:text-foreground">LINE OA</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/line-oa/rich-menus" className="hover:text-foreground">Rich Menus</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground">New Rich Menu</span>
        </nav>

        <h2 className="text-2xl font-semibold">New Rich Menu</h2>

        {/* 1. Settings */}
        <Card className="rounded border-0 bg-white">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold">1. Settings</h3>

            <div className="space-y-2">
              <Label htmlFor="menu-name">Menu name</Label>
              <Input
                id="menu-name"
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
                placeholder="e.g. Weekend Sale Menu"
              />
              <p className="text-xs text-muted-foreground">Internal label, not visible to customers.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chat-bar-text">Chat bar text</Label>
              <Input
                id="chat-bar-text"
                value={chatBarText}
                onChange={(e) => setChatBarText(e.target.value.slice(0, 14))}
                placeholder="Tap to open"
                maxLength={14}
              />
              <p className="text-xs text-muted-foreground">
                {chatBarText.length}/14 characters. Shown on the collapsed menu bar in LINE.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Menu size</Label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="menuSize"
                      checked={menuSize === 'full'}
                      onChange={() => handleMenuSizeChange('full')}
                      className="accent-primary"
                    />
                    Full
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="menuSize"
                      checked={menuSize === 'half'}
                      onChange={() => handleMenuSizeChange('half')}
                      className="accent-primary"
                    />
                    Half
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Default state</Label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="selected"
                      checked={selected}
                      onChange={() => setSelected(true)}
                      className="accent-primary"
                    />
                    Expanded
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="selected"
                      checked={!selected}
                      onChange={() => setSelected(false)}
                      className="accent-primary"
                    />
                    Collapsed
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Menu Image */}
        <Card className="rounded border-0 bg-white">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold">2. Menu Image</h3>

            {!imagePreview ? (
              <div
                className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${
                  isDragOver
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPEG or PNG, max 1MB
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supported sizes: {validSizes.map(([w, h]) => `${w}×${h}`).join(', ')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Menu preview"
                    className="w-full rounded-sm"
                  />
                  {/* Tap area overlay */}
                  {imageDimensions && (
                    <div className="absolute inset-0">
                      {computeAreaBounds(template, imageDimensions.width, imageDimensions.height).map(
                        (bounds, i) => {
                          if (i >= areaCount) return null
                          const left = `${(bounds.x / imageDimensions.width) * 100}%`
                          const top = `${(bounds.y / imageDimensions.height) * 100}%`
                          const width = `${(bounds.width / imageDimensions.width) * 100}%`
                          const height = `${(bounds.height / imageDimensions.height) * 100}%`
                          return (
                            <div
                              key={i}
                              className={`absolute border transition-colors flex items-center justify-center ${
                                hoveredArea === i
                                  ? 'border-blue-500 bg-blue-500/20'
                                  : 'border-white/60 bg-black/10'
                              }`}
                              style={{ left, top, width, height }}
                              onMouseEnter={() => setHoveredArea(i)}
                              onMouseLeave={() => setHoveredArea(null)}
                            >
                              <span className="text-white text-sm font-bold drop-shadow-md">
                                {i + 1}
                              </span>
                            </div>
                          )
                        },
                      )}
                    </div>
                  )}
                  <button
                    onClick={clearImage}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {imageDimensions && (
                  <p className="text-xs text-muted-foreground">
                    {imageDimensions.width}×{imageDimensions.height}px · {imageFile?.name}
                  </p>
                )}
              </div>
            )}

            {imageError && (
              <p className="text-sm text-red-600">{imageError}</p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileInput}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* 3. Tap Areas (visible after image upload) */}
        {imagePreview && imageDimensions && (
          <Card className="rounded border-0 bg-white">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">3. Tap Areas</h3>
                <Select value={gridTemplate} onValueChange={handleGridTemplateChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(GRID_TEMPLATES).map(([key, t]) => (
                      <SelectItem key={key} value={key}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                {Array.from({ length: areaCount }).map((_, i) => {
                  const cfg = areaConfigs[i] || { actionType: 'uri', value: '' }
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-3 p-3 rounded-sm transition-colors ${
                        hoveredArea === i ? 'bg-blue-50' : 'bg-gray-50'
                      }`}
                      onMouseEnter={() => setHoveredArea(i)}
                      onMouseLeave={() => setHoveredArea(null)}
                    >
                      <div className="w-6 h-6 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-gray-600">{i + 1}</span>
                      </div>
                      <Select
                        value={cfg.actionType}
                        onValueChange={(v) => updateAreaConfig(i, 'actionType', v)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="uri">Open URL</SelectItem>
                          <SelectItem value="message">Send message</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={cfg.value}
                        onChange={(e) => updateAreaConfig(i, 'value', e.target.value)}
                        placeholder={cfg.actionType === 'uri' ? 'https://...' : 'Message text'}
                        className="flex-1"
                      />
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/line-oa/rich-menus')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            Create Rich Menu
          </Button>
        </div>
      </div>
    </PageContainer>
  )
}
